'use client';

import { useState } from 'react';
import { toast } from '@/shared/lib/toast';
import { passesService } from '@/features/payment/api/passes';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';

/** Store-owned subscription management pages (deep links, not our pages). */
const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const GOOGLE_SUBSCRIPTIONS_URL =
  'https://play.google.com/store/account/subscriptions';

/**
 * Manage-subscription affordance + purchase-channel clarifier for an owned
 * Pro Plan card (the paywall celebration and the storefront PassCard render
 * the same block). All copy is SERVER-DRIVEN: `manage_label` /
 * `purchased_via_label` come off the API verbatim — the client composes no
 * text and adds no translation keys (API-driven messages rule).
 *
 * Routing by provider:
 *  - `paddle` → fetch a FRESH portal URL (GET /v1/paddle/portal) on every
 *    click — the URLs are temporary, never cached — and open it in a new tab.
 *  - `apple` / `google` → the store's own subscriptions page in a new tab
 *    (the store, not us, owns cross-store subscription management).
 *
 * `purchased_via_label` shows only when the provider is NOT paddle: web's own
 * rail is paddle, so a web purchase needs no channel clarifier.
 *
 * Renders nothing when the provider or the label is missing (older server
 * payloads must not break the owned card).
 */
export function ManageSubscriptionSection({
  provider,
  manageLabel,
  purchasedViaLabel,
  multipleSubscriptionsNotice,
}: {
  provider: string | null | undefined;
  manageLabel: string | null | undefined;
  purchasedViaLabel: string | null | undefined;
  /** Server-localized multi-store billing warning, shown in a NoticeBanner
   *  when the user is billed by 2+ stores at once. Null → no banner. */
  multipleSubscriptionsNotice?: string | null;
}) {
  const [isLoading, setIsLoading] = useState(false);

  // The banner (multi-store warning) can render even when there's no primary
  // provider to manage, so it's gated separately below.
  if (!provider && !multipleSubscriptionsNotice) return null;

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleManage = async () => {
    if (provider === 'apple') {
      openInNewTab(APPLE_SUBSCRIPTIONS_URL);
      return;
    }
    if (provider === 'google') {
      openInNewTab(GOOGLE_SUBSCRIPTIONS_URL);
      return;
    }
    if (provider !== 'paddle' || isLoading) return;
    setIsLoading(true);
    try {
      // Fresh portal URL per click — the signed URL is short-lived; caching a
      // previous one would open a 401 dead end.
      const portal = await passesService.getPaddlePortal();
      openInNewTab(portal.data.attributes.url);
    } catch (err) {
      // API-driven messages: surface the server error text as-is.
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const showClarifier = Boolean(purchasedViaLabel) && provider !== 'paddle';

  if (!manageLabel && !showClarifier && !multipleSubscriptionsNotice)
    return null;

  return (
    <div className="flex w-full flex-col items-center gap-1">
      {/* Multi-store double-billing warning (server-driven copy). Shown when
          the same Pro Plan is active through 2+ stores so the user can cancel
          the extras before paying twice. */}
      {multipleSubscriptionsNotice && (
        <NoticeBanner
          variant="warning"
          message={multipleSubscriptionsNotice}
          className="mb-2 w-full text-left"
        />
      )}
      {showClarifier && (
        <p className="text-card-meta text-muted-foreground">
          {purchasedViaLabel}
        </p>
      )}
      {manageLabel && (
        <button
          type="button"
          onClick={() => void handleManage()}
          disabled={isLoading}
          // Subtle link-style affordance — same treatment as the dialog footer
          // links (Terms), deliberately not a primary CTA.
          className="text-card-meta text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline disabled:opacity-60"
        >
          {manageLabel}
        </button>
      )}
    </div>
  );
}
